import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/StatusBadge';
import { MarkdownContent } from '../components/MarkdownContent';
import { cn } from '../lib/utils';
import { getProductDisplayName, getUrlsFromJsonb } from '../lib/helpers';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Search, 
  MessageSquare, 
  ThumbsUp, 
  ThumbsDown, 
  Loader2,
  ExternalLink,
  FileText,
  Info
} from 'lucide-react';

export default function ProductResearchDetail() {
  const { ecomId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [review, setReview] = useState(null);
  const [winningDetail, setWinningDetail] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [productRes, reviewRes, winningRes] = await Promise.all([
        supabase.from('products').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('product_reviews').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('winning_product_details').select('ecom_id, product_name').eq('ecom_id', ecomId).single(),
      ]);

      setProduct(productRes.data);
      setReview(reviewRes.data);
      setWinningDetail(winningRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load product research data');
    } finally {
      setLoading(false);
    }
  }, [ecomId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <Layout productName="Loading...">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <div className="text-center py-16">
          <Search className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600 mb-4">Product not found</p>
          <button
            onClick={() => navigate('/product-research')}
            className="px-6 py-2 bg-cyan-500 text-white rounded-lg"
          >
            Back to Product Research
          </button>
        </div>
      </Layout>
    );
  }

  const displayName = getProductDisplayName(ecomId, winningDetail ? [winningDetail] : []);
  const images = getUrlsFromJsonb(product.image_urls);
  const heroImage = images[0];

  return (
    <Layout productName={displayName}>
      <div data-testid="product-research-detail">
        {/* Header */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <button
                onClick={() => navigate('/product-research')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              
              {/* Image */}
              <div className="w-12 h-12 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                {heroImage ? (
                  <img src={heroImage} alt={displayName} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Search className="w-5 h-5 text-slate-400" />
                  </div>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <h1 className="text-lg font-bold text-[#1E1B4B] font-['Manrope'] truncate">
                  {displayName}
                </h1>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <StatusBadge status={product.product_status} lane="research" />
                  <span className="text-xs text-slate-500 capitalize">{product.platform}</span>
                </div>
              </div>
            </div>
            
            {product.url && (
              <a
                href={product.url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg flex items-center gap-2 text-sm self-start sm:self-auto"
              >
                <ExternalLink className="w-4 h-4" />
                <span className="hidden sm:inline">View Original</span>
                <span className="sm:hidden">View</span>
              </a>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Product Details */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Product Details
            </h4>
            {product.markdown ? (
              <MarkdownContent 
                content={product.markdown} 
                title="Product Details"
                maxHeight={300}
              />
            ) : (
              <p className="text-slate-400 text-sm italic">No product details yet</p>
            )}
            
            {product.additional_info && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <h5 className="text-sm font-semibold text-slate-600 mb-2 flex items-center gap-2">
                  <Info className="w-3 h-3" />
                  Additional Info
                </h5>
                <MarkdownContent 
                  content={product.additional_info} 
                  title="Additional Information"
                  maxHeight={150}
                />
              </div>
            )}
          </div>

          {/* Reviews Analysis */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Reviews Analysis
            </h4>
            {review ? (
              <div className="space-y-4">
                {/* Positive Reviews */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <ThumbsUp className="w-4 h-4 text-emerald-500" />
                    <span className="text-sm font-medium text-emerald-700">Positive Reviews</span>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-1">
                    <MarkdownContent 
                      content={review.positive_reviews || 'Not analyzed'} 
                      title="Positive Reviews Analysis"
                      maxHeight={150}
                      className="[&>div]:bg-emerald-50"
                    />
                  </div>
                </div>
                
                {/* Negative Reviews */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <ThumbsDown className="w-4 h-4 text-red-500" />
                    <span className="text-sm font-medium text-red-700">Negative Reviews</span>
                  </div>
                  <div className="bg-red-50 rounded-lg p-1">
                    <MarkdownContent 
                      content={review.negative_reviews || 'Not analyzed'} 
                      title="Negative Reviews Analysis"
                      maxHeight={150}
                      className="[&>div]:bg-red-50"
                    />
                  </div>
                </div>
                
                {/* Product Feedback */}
                {review.product_feedback && (
                  <div>
                    <h5 className="text-sm font-semibold text-slate-600 mb-2">Product Feedback</h5>
                    <MarkdownContent 
                      content={review.product_feedback} 
                      title="Product Feedback"
                      maxHeight={120}
                    />
                  </div>
                )}
              </div>
            ) : (
              <p className="text-slate-400 text-sm italic">No reviews analyzed yet</p>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
